import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import { canCarryPregnancy } from '../../pedigree-tool/pedigree/config';
import {
  derivedGravidity,
  derivedParity
} from '../../pedigree-tool/pedigree/pregnancyObstetrics';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import { normalizeFetuses } from '../../pedigree-tool/pedigree/pregnancyNormalize';
import { findBornChild } from '../../pedigree-tool/pedigree/liveBirthInheritance';
import {
  PregnancyModal,
  DeleteConfirm,
  usePregnancyCrud,
  OUTCOME_LABELS,
  spouseOptionsFor
} from '../../pedigree-tool/canvas-tools/options/pregnancyEditor';
import {
  PREG_ACCENT,
  recordCardTint,
  recordCardTintBorder
} from './clinical-notes-section/recordCardStyles';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// "Reproductive" accordion on a carrier's per-person sidebar (Track B). Lists the carrier's
// pregnancy RECORDS (Pregnancy/PregnancyChanged) as tiles styled to match the Clinical
// Records tiles (coloured left bar + low-opacity tint + type as the title), for consistency
// across the app. The tile is a plain clickable card — click opens the shared editor modal
// (add/edit + hide + delete all live in the modal). The carrier here is a real person; the
// pregnancies/donors it produces are DERIVED render nodes, never members. Gated into the
// sidebar on config.ENABLE_REPRO_NODES (see util.getUserSidebarItems).

const CONCEIVED_LABEL = {
  partner: 'Partner',
  spermDonor: 'Sperm donor',
  eggDonor: 'Egg donor',
  embryoDonor: 'Donated embryo',
  partnerAndDonor: 'Partner + donor'
};

// The outcome's label from the editor's own list, translated; a value the list
// does not hold is shown as stored.
const outcomeLabel = (outcome, t) => {
  const known = OUTCOME_LABELS.find(([value]) => value === outcome);
  return known ? t(known[1]) : outcome;
};

// THIRD copy of the private 'en-GB' date formatter TGCLOUD-1707 is about (the
// others were in fetusEventSubject.js and BornOfPregnancyNotice.jsx), so it
// ignored Settings → Localisation the same way. Replaced by the house pair.
//
// `dateOfRecord` and `lastMenstrualDate` are `type: Date` — INSTANT columns fed
// from a DatePicker, so their calendar day must be read with LOCAL components
// (toLocalDateFromInstant). The legacy `doOutcome` is a `type: String` column, and
// toLocalDateFromInstant delegates a bare 'YYYY-MM-DD' to toLocalDate, so one
// call site handles both classes correctly. Returns null, not '', because the
// caller chains with `||`.
const useRecordDateFormatter = () => {
  const fmt = useDateFormatter();
  return React.useCallback(
    (v) => fmt(toLocalDateFromInstant(v)) || null,
    [fmt]
  );
};

// The clinicians recorded against a pregnancy, in Desktop's column order (SS-12), joined so the
// tile stays one compact line rather than a table.
const cliniciansOf = (record) =>
  [record.staffMember, record.obstetrician, record.otherDoctor]
    .filter(Boolean)
    .join(' · ');

// How a linked person reads in the navigation lines — a name where there is one, otherwise the
// pedigree number, which is what an unnamed member is known by on the chart.
const personLabel = (member, t) =>
  member
    ? [member.firstName, member.lastName].filter(Boolean).join(' ') ||
      t('Person {{pid}}', { pid: member.pid })
    : '';

function PregnancyTile({ record, onOpen, bornChild, onGoToChild }) {
  const t = useAutoTranslation();

  const fmtDate = useRecordDateFormatter();
  const hidden = !!record.hidden;
  const weeks = record.gestationalWeeks
    ? ` · ${t('{{weeks}}w', { weeks: record.gestationalWeeks })}`
    : '';
  // Fetus count is DERIVED, never stored (REQ-B4). A record written before fetuses existed has no
  // array and is one implicit fetus.
  const fetusCount =
    Array.isArray(record.fetuses) && record.fetuses.length
      ? record.fetuses.length
      : 1;
  const recordDate = fmtDate(record.dateOfRecord) || fmtDate(record.doOutcome);
  const lmp = fmtDate(record.lastMenstrualDate);
  const clinicians = cliniciansOf(record);
  return (
    <Card
      sx={{
        border: `1px solid ${recordCardTintBorder(PREG_ACCENT)}`,
        borderLeft: `3px solid ${PREG_ACCENT}`,
        backgroundColor: recordCardTint(PREG_ACCENT),
        boxShadow: 'none',
        borderRadius: '6px',
        opacity: hidden ? 0.55 : 1,
        transition: 'transform .15s ease, box-shadow .15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(31, 42, 51, .13)'
        }
      }}
    >
      <CardActionArea onClick={onOpen}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: PREG_ACCENT,
              lineHeight: 1
            }}
          >
            {t('Pregnancy')}
            {hidden ? ` · ${t('hidden')}` : ''}
            {recordDate ? ` · ${recordDate}` : ''}
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            <div>
              {fetusCount > 1
                ? `${t('{{fetusCount}} fetuses', { fetusCount })} · `
                : ''}
              {outcomeLabel(record.outcome, t)}
              {weeks}
            </div>
            <div>
              {t('Conceived with')}{' '}
              {t(CONCEIVED_LABEL[record.conceivedWith]) || '—'}
            </div>
            {lmp ? (
              <div>
                {t('LMP')} {lmp}
              </div>
            ) : null}
            {clinicians ? <div>{clinicians}</div> : null}
            {record.karyotype ? <div>{record.karyotype}</div> : null}
            {record.comments ? (
              <div style={{ fontStyle: 'italic' }}>{record.comments}</div>
            ) : null}
          </Typography>
        </CardContent>
      </CardActionArea>
      {/* REQ-I6 direction 1 — pregnancy → the person born of it. Outside the CardActionArea so the
          link is its own target and does not also open the editor. */}
      {bornChild && onGoToChild && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Button
            size="small"
            onClick={() => onGoToChild(bornChild.pid)}
            sx={{ px: 0 }}
          >
            {t('Go to {{personLabel}}', {
              personLabel: personLabel(bornChild, t)
            })}
          </Button>
        </Box>
      )}
    </Card>
  );
}

export default function ReproductiveSection({
  data,
  user,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  modificationHistoryHandle,
  isDisabled,
  // Set when the user double-clicked a fetus symbol on the pedigree (REQ-H4): open THAT record's
  // editor. Cleared via onOpenRecordHandled so re-selecting the carrier later does not reopen it.
  openRecordId,
  onOpenRecordHandled,
  // Select a member on the chart by pid (REQ-I6 navigation). Selection lives in ChartBox, so this
  // posts a request rather than reaching into the canvas.
  onSelectMember,
  // Open a prenatal clinical record scoped to a fetus (decision 21). The note form lives in the
  // Clinical Records section, so this hands the scope to the sidebar rather than opening it here.
  onAddFetusEvent
}) {
  const t = useAutoTranslation();

  const { setSnack } = React.useContext(SnackContext);
  // The family's canonical diagnoses — so a condition already recorded on a relative keeps its colour
  // when it is added to a fetus (decision 20).
  const { content } = React.useContext(AppContext);
  const familyDiagnoses = content?.pedigreeSelectedUsers?.diagnoses || [];
  // Attribution for fetus diagnoses/phenotypes — the signed-in clinician.
  const recordedBy = useLoggedInUserName();
  // A modal target: { record } for edit, { record: null } for add; null = closed.
  const [modalTarget, setModalTarget] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const {
    busy,
    submitPregnancy,
    deletePregnancy,
    convertFetusToPerson,
    createDonorPerson
  } = usePregnancyCrud({
    memberRecords,
    setMemberRecords,
    setPedigreeState,
    setSnack,
    user
  });

  // The carrier is the selected member as held in memberRecords (source of truth for _id +
  // pregnancies); fall back to `data` so the panel still renders before records hydrate.
  const carrier =
    (memberRecords || []).find((m) => m.pid === data?.pid) || data;
  const pregnancies = carrier?.pregnancies || [];
  const canCarry = canCarryPregnancy(data);
  const spouseOptions = spouseOptionsFor(carrier, memberRecords);

  // Per record, the person born of it — REQ-I6 direction 1. Derived, never stored: `bornMemberId` is
  // the single link. The OTHER direction (the pregnancy this person was born of) moved to the PERSON
  // section as BornOfPregnancyNotice — TGCLOUD-1617 item 5, so this list holds only the individual's
  // own pregnancies and nothing that merely mentions them.
  const findBornChildFor = (record) => {
    const fetuses = normalizeFetuses(record);
    for (const fetus of fetuses) {
      const child = findBornChild(memberRecords, fetus);
      if (child) return child;
    }
    return null;
  };

  // Honour a double-click on the pedigree (REQ-H4). The request is cleared as soon as it is acted
  // on, so closing the modal and reselecting the carrier does not spring it open again. If the
  // record isn't in this carrier's list the request is dropped (still cleared) rather than opening
  // the wrong editor.
  React.useEffect(() => {
    if (!openRecordId) return;
    const target = pregnancies.find(
      (p) => String(p._id) === String(openRecordId)
    );
    if (target) setModalTarget({ record: target });
    onOpenRecordHandled?.();
  }, [openRecordId, pregnancies, onOpenRecordHandled]);

  // Return the result; the modal decides whether to close or prompt to add the live-birth
  // child (it calls onClose itself).
  const handleSubmit = (formValues) =>
    submitPregnancy(carrier, modalTarget?.record, formValues);

  const handleDelete = async () => {
    const ok = await deletePregnancy(carrier, deleteTarget);
    if (ok) setDeleteTarget(null);
  };

  return (
    <Box>
      <Stack spacing={1}>
        {/* Gravidity / parity DERIVED from the records (REQ-A3, Tim's decision 11). Shown as
            read-only context, not as an editable field: `parity` is a live CanRisk risk-model input
            (canriskSerializer) with its own home in the Risk Factors form, and a second editable
            parity would give the risk calculation two sources of truth. Tim explicitly allowed
            keeping the field itself manual — so the clinician sees what the records imply here and
            enters the number they accept in Risk Factors. Parity counts per PREGNANCY, so a twin
            delivery is one parous event. */}
        {pregnancies.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t(
              'From these records: gravidity {{gravidity}} · parity {{parity}}',
              {
                gravidity: derivedGravidity(pregnancies) ?? 0,
                parity: derivedParity(pregnancies) ?? 0
              }
            )}
          </Typography>
        )}

        {pregnancies.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('No pregnancies recorded.')}
          </Typography>
        ) : (
          pregnancies.map((record) => (
            <PregnancyTile
              key={String(record._id)}
              record={record}
              onOpen={() => setModalTarget({ record })}
              bornChild={findBornChildFor(record)}
              onGoToChild={onSelectMember}
            />
          ))
        )}

        {/* When the gate is closed but records ALREADY exist, the plain tooltip reads as a
            contradiction ("why can't I add one when there are two here?"). Those records can arrive
            from the FHQ, whose gate is broader ("not male") than the pedigree's (sex assigned at
            birth = female) — so say so rather than leaving the clinician to guess. Tim's decision 10
            keeps both gates as they are; this is the wording that makes the asymmetry legible. */}
        <Tooltip
          title={
            canCarry
              ? ''
              : pregnancies.length > 0
              ? t(
                  'These pregnancies were recorded elsewhere (e.g. the questionnaire). To add another here, set Sex Assigned at Birth to Female.'
                )
              : t(
                  'Set Sex Assigned at Birth to Female before adding a pregnancy'
                )
          }
          placement="top"
        >
          <span>
            <Button
              startIcon={<AddIcon />}
              size="small"
              variant="outlined"
              disabled={!canCarry || busy || isDisabled}
              onClick={() => setModalTarget({ record: null })}
              fullWidth
            >
              {t('Add fetus…')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {modalTarget && (
        <PregnancyModal
          record={modalTarget.record}
          spouseOptions={spouseOptions}
          familyDiagnoses={familyDiagnoses}
          recordedBy={recordedBy}
          carrier={carrier}
          onAddFetusEvent={
            onAddFetusEvent
              ? (scope) => {
                  // Close this modal first: the record form opens in another section, and leaving
                  // two dialogs stacked would trap the clinician behind the wrong one.
                  setModalTarget(null);
                  onAddFetusEvent(scope);
                }
              : undefined
          }
          onSubmit={handleSubmit}
          onClose={() => setModalTarget(null)}
          onConvert={(rec, gender, fetusIndex) =>
            convertFetusToPerson(carrier, rec, gender, fetusIndex)
          }
          memberRecords={memberRecords}
          onCreateDonorPerson={createDonorPerson}
          onDelete={
            modalTarget.record
              ? () => {
                  const rec = modalTarget.record;
                  setModalTarget(null);
                  setDeleteTarget(rec);
                }
              : undefined
          }
          historyAction={
            modalTarget.record && modificationHistoryHandle
              ? () =>
                  modificationHistoryHandle(
                    CONSTANTS.auditLogType.reproductive,
                    {},
                    String(modalTarget.record._id)
                  )
              : undefined
          }
        />
      )}
      {deleteTarget && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          deleting={busy}
        />
      )}
    </Box>
  );
}
